import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Alert, CircularProgress } from "@mui/material";
import * as XLSX from "xlsx";
import api from "../../../utils/api";
import { CalendarType } from "../../../shared/components/FormType";
import { StyledButton } from "../../../shared/styled/TitleBar";
import { DailyPlan, planWorkbook, vietnamToday } from "./plan";
import PlanDocument from "./PlanDocument";
import { DateField, PlanToolbar, PlanWrapper } from "./Styled";
import { printDailyPlan } from "./print";

export default function DailyDeliveryPlan({ actionsContainer }: { actionsContainer: HTMLDivElement | null }) {
    const [date, setDate] = useState(vietnamToday);
    const [data, setData] = useState<DailyPlan | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [printError, setPrintError] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [retry, setRetry] = useState(0);
    useEffect(() => {
        let active = true;
        setLoading(true);
        setFailed(false);
        setData(null);
        setPrintError(false);
        api.get(`/product-flow/v2/delivery-trips/daily-plan/?date=${date}`)
            .then((result) => {
                if (active) setData(result as DailyPlan);
            })
            .catch(() => {
                if (active) setFailed(true);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [date, retry]);
    const print = async () => {
        if (!data) return;
        setPrinting(true);
        setPrintError(false);
        try {
            await printDailyPlan(data);
        } catch {
            setPrintError(true);
        } finally {
            setPrinting(false);
        }
    };
    return (
        <PlanWrapper>
            {actionsContainer && createPortal(
                <>
                    <StyledButton variant="outlined" disabled={loading} onClick={() => setRetry((n) => n + 1)}>
                        Tải lại
                    </StyledButton>
                    <StyledButton variant="outlined" disabled={!data || loading || printing} onClick={print}>
                        {printing ? "Đang chuẩn bị…" : "In lịch"}
                    </StyledButton>
                    <StyledButton
                        variant="contained"
                        disabled={!data || loading}
                        onClick={() => data && XLSX.writeFile(planWorkbook(data), `Lich-giao-hang-${data.date}.xlsx`)}
                    >
                        Xuất Excel
                    </StyledButton>
                </>,
                actionsContainer,
            )}
            <PlanToolbar>
                <DateField>
                    <span>Ngày giao hàng</span>
                    <div className="date-control">
                        <CalendarType
                            field={{ key: "date", label: "Ngày giao hàng", type: "calendar" }}
                            formData={{ date }}
                            onChange={(value) => {
                                if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) setDate(value);
                            }}
                            onBlur={() => {}}
                            touched={{}}
                            errors={{}}
                        />
                    </div>
                </DateField>
            </PlanToolbar>
            {loading && <CircularProgress size={24} aria-label="Đang tải lịch" />}
            {failed && <Alert severity="error">Không tải được lịch giao hàng. Vui lòng thử lại.</Alert>}
            {printError && <Alert severity="error">Không mở được bản in. Vui lòng thử lại.</Alert>}
            {data && <PlanDocument data={data} />}
        </PlanWrapper>
    );
}
